import omit from 'lodash/omit'
import { useCallback, useEffect, useState } from 'react'
import useDebouncedEffect from '~/hooks/useDebouncedEffect'
import { initializeFilterValues, transformFilters } from './utils'

/**
 * Hook for managing the filters and their values and params. This hook is
 * lifted into context by `constate` and should not be used directly.
 */
export const useFiltersState = ({
  filters,
  isConfigLoading = false,
  keepNullValues = false,
  applyDebounceMs = 1000,
  onParamsChange = () => {}
}) => {
  const [areFiltersInitializing, setAreFiltersInitializing] = useState(true)

  const [values, setValues] = useState(() => initializeFilterValues(filters))

  // params are the value of each filter transformed into url search params. if
  // the value is "empty" (empty array, empty string, etc.), it isn't included
  // in params
  const [params, setParams] = useState(() => transformFilters(filters, values, keepNullValues))

  useEffect(() => {
    setValues(initializeFilterValues(filters))
  }, [filters])

  /**
   * Callback for applying the current filter values.
   * apply just means to set the params to a new value.
   * consumers can figure out what to do with that change.
   * */
  const apply = useCallback(() => {
    setParams(transformFilters(filters, values, keepNullValues))
    if (!isConfigLoading) {
      setAreFiltersInitializing(false)
    }
  }, [values, filters, keepNullValues])

  useDebouncedEffect(
    (_, apply) => {
      apply()
    },
    { delay: process.env.NODE_ENV === 'test' ? 1 : applyDebounceMs, initial: true },
    [values],
    [apply]
  )

  useEffect(() => {
    onParamsChange(params)
  }, [params])

  /** Callback for clearing filter values to an empty state. */
  const clear = useCallback(() => {
    setValues(initializeFilterValues(filters, { useEmptyValues: true }))
  }, [filters])

  /**
   * Callback for updating a filter's value.
   *
   * @param {string} id The id of the filter to update.
   * @param {*} value The value to update the filter with.
   */
  const updateFilterValue = useCallback((id, value) => {
    setValues(values => {
      if (value == null || value === '') return omit(values, id)
      return { ...values, [id]: value }
    })
  }, [])

  const restoreSavedFilter = useCallback(savedValues => {
    setValues(savedValues)
  }, [])

  return { filters, values, params, apply, clear, updateFilterValue, restoreSavedFilter, areFiltersInitializing }
}
